/// <reference types="../vite-env.d.ts" />

import { App } from './app.js';
import { showStatusNotification } from './utils.js';
import { network, device, storage } from './utils.js';
import { initializeAuth, ensureAuthenticated, authEnabled, getCurrentUserName, logout } from './auth.js';
import { showConfirmModal } from './components/confirm-modal.js';

// Global application instance
let app: App;

// Application configuration
const config = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  signalRHubUrl: import.meta.env.VITE_SIGNALR_HUB_URL || '/api',
  enablePWA: false,
  enableOfflineSupport: true,
  enableAnalytics: false
};

// Initialize the application
async function initializeApp(): Promise<void> {
  try {
    console.log('Initializing PDF PDF Protocol Viewer...');

    if (authEnabled()) {
      await initializeAuth();
      await ensureAuthenticated();
    }
    
    // Service worker disabled for auth compatibility
    
    // Initialize App
    app = new App();
    
    // Mount the app to the DOM
    const targetElement = document.getElementById('app') || document.body;
    app.mount(targetElement);

    if (authEnabled()) {
      setupAuthUi();
    }
    
    // Set up network status monitoring
    setupNetworkMonitoring();
    
    // Set up device-specific optimizations
    setupDeviceOptimizations();
    
    // Load user preferences
    loadUserPreferences();
    
    // Set up error handling
    setupErrorHandling();
    
    console.log('PDF PDF Protocol Viewer initialized successfully');
    
    showStatusNotification('PDF PDF Protocol Viewer ready!', 'success', 2000);
    
  } catch (error) {
    console.error('Failed to initialize PDF PDF Protocol Viewer:', error);
    showStatusNotification('Failed to initialize application. Please refresh the page.', 'error', 5000);
  }
}

function setupAuthUi(): void {
  const authArea = document.getElementById('auth-area');
  if (!authArea) {
    return;
  }

  const userName = getCurrentUserName();
  if (!userName) {
    authArea.innerHTML = '';
    return;
  }

  authArea.innerHTML = '';

  const userSpan = document.createElement('span');
  userSpan.textContent = userName;
  userSpan.style.cssText = 'font-size: 0.9rem; color: var(--text-secondary, #64748b);';

  const logoutButton = document.createElement('button');
  logoutButton.textContent = 'Sign out';
  logoutButton.style.cssText = 'margin-left: 0.75rem; padding: 0.4rem 0.7rem; border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; cursor: pointer;';
  logoutButton.addEventListener('click', () => {
    showConfirmModal({
      title: 'Sign out?',
      message: 'You will be returned to the sign-in page.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Cancel',
      onConfirm: () => {
        void logout();
      }
    });
  });

  authArea.appendChild(userSpan);
  authArea.appendChild(logoutButton);
}

// Service worker registration removed for auth compatibility

// Set up network status monitoring
function setupNetworkMonitoring(): void {
  if (!config.enableOfflineSupport) return;
  
  network.addOnlineListener(() => {
    console.log('Network connection restored');
    showStatusNotification('Connection restored', 'success', 3000);
    
    // Refresh data when coming back online
    if (app) {
      app.refresh();
    }
  });
  
  network.addOfflineListener(() => {
    console.log('Network connection lost');
    showStatusNotification('You are offline. Some features may be limited.', 'warning', 5000);
  });
}

// Set up device-specific optimizations
function setupDeviceOptimizations(): void {
  // Add device-specific classes to body
  const body = document.body;
  
  if (device.isMobile()) {
    body.classList.add('device-mobile');
  }
  
  if (device.isTablet()) {
    body.classList.add('device-tablet');
  }
  
  if (device.isTouch()) {
    body.classList.add('device-touch');
  }
  
  if (device.isSafari()) {
    body.classList.add('browser-safari');
  }
  
  // Optimize for iPad specifically
  if (device.isTablet() && device.isSafari()) {
    body.classList.add('device-ipad');
    
    // Add viewport meta tag for better iPad experience
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }
  }
}

// Load user preferences from localStorage
function loadUserPreferences(): void {
  try {
    const preferences = storage.get('pdfBrowserPreferences', {
      theme: 'light',
      pageSize: 12,
      defaultSort: 'date-desc',
      enableNotifications: true
    });
    
    // Apply theme
    if (preferences && preferences.theme === 'dark') {
      document.body.classList.add('theme-dark');
    }
    
    // Store preferences for use in components
    (window as any).pdfBrowserPreferences = preferences;
    
  } catch (error) {
    console.warn('Failed to load user preferences:', error);
  }
}

// Set up global error handling
function setupErrorHandling(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    showStatusNotification('An unexpected error occurred. Please try again.', 'error', 5000);
    event.preventDefault();
  });
  
  // Handle JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('JavaScript error:', event.error);
    showStatusNotification('An error occurred. Please refresh the page.', 'error', 5000);
  });
  
  // Handle beforeunload to save state
  window.addEventListener('beforeunload', () => {
    try {
      // Save current state
      if (app) {
        const currentFilters = app.getCurrentFilters();
        storage.set('pdfBrowserState', {
          filters: currentFilters,
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.warn('Failed to save state before unload:', error);
    }
  });
}

// Handle visibility change (tab switching)
function setupVisibilityHandling(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Tab became visible, refresh data if needed
      const lastUpdate = storage.get('lastDataUpdate', 0);
      const now = Date.now();
      
      // Refresh if it's been more than 5 minutes
      if (lastUpdate && now - lastUpdate > 5 * 60 * 1000 && app) {
        app.refresh();
        storage.set('lastDataUpdate', now);
      }
    }
  });
}

// Handle keyboard shortcuts
function setupKeyboardShortcuts(): void {
  document.addEventListener('keydown', (event) => {
    // Only handle shortcuts when not in input fields
    if (event.target instanceof HTMLInputElement || 
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement) {
      return;
    }
    
    // Ctrl/Cmd + F: Focus search
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      const searchInput = document.getElementById('searchInput') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
        searchInput.select();
      }
    }
    
    // Ctrl/Cmd + R: Refresh
    if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
      event.preventDefault();
      if (app) {
        app.refresh();
      }
    }
    
    // Ctrl/Cmd + K: Clear search
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      const searchInput = document.getElementById('searchInput') as HTMLInputElement;
      if (searchInput) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input'));
      }
    }
  });
}

// Performance monitoring
function setupPerformanceMonitoring(): void {
  if (config.enableAnalytics) {
    // Monitor page load performance
    window.addEventListener('load', () => {
      const loadTime = performance.now();
      console.log(`Page loaded in ${loadTime.toFixed(2)}ms`);
      
      // Report to analytics if configured
      if (typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'timing_complete', {
          name: 'load',
          value: Math.round(loadTime)
        });
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeApp();
    setupVisibilityHandling();
    setupKeyboardShortcuts();
    setupPerformanceMonitoring();
  });
} else {
  // DOM is already ready
  initializeApp();
  setupVisibilityHandling();
  setupKeyboardShortcuts();
  setupPerformanceMonitoring();
}

// Export for debugging
// Note: app will be assigned in initializeApp
if (typeof window !== 'undefined') {
  (window as any).getApp = () => app;
}
